import { useEffect, useRef, useImperativeHandle, forwardRef, useState, useId } from 'react'
import ApexCharts from 'apexcharts'
import './DonutChart.css'

interface DonutChartProps {
  labels: string[]
  series: number[]
  title?: string
  height?: number
  colors?: string[]
  showLegend?: boolean
  type?: 'donut' | 'pie'
  noDataMessage?: string
  exportFilename?: string
  legendPosition?: 'top' | 'right' | 'bottom' | 'left'
  legendOffsetX?: number
  legendOffsetY?: number
  legendFloating?: boolean
  legendHorizontalAlign?: 'left' | 'center' | 'right'
  pieOffsetX?: number
  pieOffsetY?: number
  showInfoIcon?: boolean
  dataLabelColor?: string
  dataLabelDropShadow?: boolean
  dataLabelOffset?: number
  isLoading?: boolean
  useCustomLegend?: boolean
  legendFontSize?: string
  legendFontWeight?: string
  legendMarkerSize?: string
  legendItemMarginHorizontal?: number
  legendItemMarginVertical?: number
  legendLineHeight?: number | string
  legendGap?: string
  legendMarginTop?: string
  minAngleToShowLabel?: number
  dataLabelDecimalPrecision?: number
  dataLabelFontSize?: string
  dataLabelFontWeight?: string
  tooltipCounts?: number[]
}

const DEFAULT_COLORS = ['#0072B2', '#009E73', '#D55E00', '#CC79A7', '#F0E442', '#56B4E9', '#E69F00', '#999999']

export interface DonutChartHandle {
  exportToSVG: () => void
  exportToCSV: () => void
}

const DonutChart = forwardRef<DonutChartHandle, DonutChartProps>(({
  labels,
  series,
  title,
  height = 250,
  colors = DEFAULT_COLORS,
  showLegend = true,
  type = 'donut',
  noDataMessage = 'No data available',
  exportFilename = 'chart',
  legendPosition = 'bottom',
  legendOffsetX = 0,
  legendOffsetY = 0,
  legendFloating = false,
  legendHorizontalAlign = 'left',
  pieOffsetX = 0,
  pieOffsetY = 0,
  showInfoIcon = false,
  dataLabelColor = '#fff',
  dataLabelDropShadow = true,
  dataLabelOffset = 0,
  isLoading = false,
  useCustomLegend = false,
  legendFontSize = '12px',
  legendFontWeight = '400',
  legendMarkerSize = '10px',
  legendItemMarginHorizontal = 5,
  legendItemMarginVertical = 0,
  legendLineHeight,
  legendGap = '4px',
  legendMarginTop = '0px',
  minAngleToShowLabel = 10,
  dataLabelDecimalPrecision = 0,
  dataLabelFontSize = '14px',
  dataLabelFontWeight = 'bold',
  tooltipCounts = []
}, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<ApexCharts | null>(null)
  const instanceClass = `donut-chart-${useId().replace(/:/g, '')}`

  const [isMobile, setIsMobile] = useState(
    window.matchMedia('(max-width: 768px)').matches
  )

  useEffect(() => {
    const mediaQuery: MediaQueryList = window.matchMedia('(max-width: 768px)')
    const handleChange = (e: MediaQueryListEvent): void => {
      setIsMobile(e.matches)
    }
    setIsMobile(mediaQuery.matches)
    mediaQuery.addEventListener('change', handleChange)
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  useImperativeHandle(ref, () => ({
    exportToSVG: () => {
      if (chartInstance.current) {
        (chartInstance.current as any).exports?.exportToSVG?.()
      }
    },
    exportToCSV: () => {
      if (chartInstance.current) {
        (chartInstance.current as any).exports?.exportToCSV?.()
      }
    }
  }))

  const safeSeries = series.map(v => (isNaN(v) || v == null ? 0 : v))

  useEffect(() => {
    if (!chartRef.current) return

    const shouldShowApexLegend = showLegend && !useCustomLegend

    const options: ApexCharts.ApexOptions = {
      chart: {
        type,
        height,
        animations: { enabled: true },
        toolbar: {
          show: true,
          tools: { download: true },
          export: {
            csv: { filename: exportFilename },
            svg: { filename: exportFilename },
            png: { filename: exportFilename },
          }
        },
      },
      series: safeSeries,
      labels,
      colors,
      stroke: {
        show: true,
        colors: ['#fff'],
        width: 2,
      },
      legend: {
        show: shouldShowApexLegend,
        position: legendPosition,
        horizontalAlign: legendHorizontalAlign,
        floating: legendFloating,
        fontSize: legendFontSize,
        fontWeight: parseInt(legendFontWeight) || 400,
        fontFamily: 'Poppins, Segoe UI, sans-serif',
        labels: { colors: '#292929' },
        offsetX: legendOffsetX,
        offsetY: legendOffsetY,
        itemMargin: {
          horizontal: legendItemMarginHorizontal,
          vertical: legendItemMarginVertical,
        },
        markers: {
          width: parseInt(legendMarkerSize) || 10,
          height: parseInt(legendMarkerSize) || 10,
          radius: 12,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number, opts: any) => {
          if (tooltipCounts.length > 0 && opts?.seriesIndex !== undefined) {
            const totalCount = tooltipCounts.reduce((s: number, c: number) => s + c, 0)
            if (totalCount > 0) {
              const precision = dataLabelDecimalPrecision > 0 ? dataLabelDecimalPrecision : 0
              const factor = Math.pow(10, precision)
              const rawPcts = tooltipCounts.map((c: number) => (c / totalCount) * 100)
              const floored = rawPcts.map((p: number) => Math.floor(p * factor) / factor)
              const remainders = rawPcts.map((p: number) => p * factor - Math.floor(p * factor))
              const flooredSum = floored.reduce((s: number, v: number) => s + Math.round(v * factor), 0)
              const target = Math.round(100 * factor)
              let deficit = target - flooredSum
              const indices = [...Array(floored.length).keys()].sort((a: number, b: number) => remainders[b] - remainders[a])
              const adjusted = [...floored]
              for (const idx of indices) {
                if (deficit <= 0) break
                adjusted[idx] = Math.round((adjusted[idx] + 1 / factor) * factor) / factor
                deficit--
              }
              const displayPct = adjusted[opts.seriesIndex]
              if (precision > 0) {
                const formatted = displayPct.toFixed(precision)
                return `${formatted.replace(/\.0+$/, '')}%`
              }
              return `${Math.round(displayPct)}%`
            }
          }
          if (dataLabelDecimalPrecision > 0) {
            const formatted = val.toFixed(dataLabelDecimalPrecision)
            const cleaned = formatted.replace(/\.0+$/, '')
            return `${cleaned}%`
          }
          return `${Math.round(val)}%`
        },
        style: {
          fontSize: isMobile ? '10px' : dataLabelFontSize,
          fontWeight: dataLabelFontWeight,
          colors: [dataLabelColor],
        },
        dropShadow: {
          enabled: dataLabelDropShadow,
          top: 1,
          left: 1,
          blur: 1,
          color: '#000',
          opacity: 0.45,
        },
      },
      plotOptions: {
        pie: {
          offsetX: pieOffsetX,
          offsetY: pieOffsetY,
          dataLabels: {
            offset: dataLabelOffset,
            minAngleToShowLabel: 0,
          },
          donut: {
            size: '65%',
            labels: {
              show: type === 'donut',
              total: {
                show: true,
                label: 'Total',
                formatter: () => {
                  const total = tooltipCounts.length > 0
                    ? tooltipCounts.reduce((a: number, b: number) => a + b, 0)
                    : safeSeries.reduce((a, b) => a + b, 0)
                  return total.toString()
                },
              },
            },
          },
        },
      },
      states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } },
      },
      tooltip: tooltipCounts.length > 0 ? {
        custom: function ({ seriesIndex, w }: { seriesIndex: number; w: any }) {
          const label = w.config.labels[seriesIndex] || ''
          const color = w.config.colors[seriesIndex] || '#333'
          const count = tooltipCounts[seriesIndex] ?? 0
          const totalCount = tooltipCounts.reduce((s: number, c: number) => s + c, 0)
          let pctStr = '0%'
          if (totalCount > 0) {
            const precision = dataLabelDecimalPrecision > 0 ? dataLabelDecimalPrecision : 0
            const factor = Math.pow(10, precision)
            const rawPcts = tooltipCounts.map((c: number) => (c / totalCount) * 100)
            const floored = rawPcts.map((p: number) => Math.floor(p * factor) / factor)
            const remainders = rawPcts.map((p: number) => p * factor - Math.floor(p * factor))
            const flooredSum = floored.reduce((s: number, v: number) => s + Math.round(v * factor), 0)
            const target = Math.round(100 * factor)
            let deficit = target - flooredSum
            const indices = [...Array(floored.length).keys()].sort((a: number, b: number) => remainders[b] - remainders[a])
            const adjusted = [...floored]
            for (const idx of indices) {
              if (deficit <= 0) break
              adjusted[idx] = Math.round((adjusted[idx] + 1 / factor) * factor) / factor
              deficit--
            }
            const displayPct = adjusted[seriesIndex]
            if (precision > 0) {
              pctStr = displayPct.toFixed(precision).replace(/\.0+$/, '') + '%'
            } else {
              pctStr = Math.round(displayPct) + '%'
            }
          }
          return `<div style="background: ${color}; color: #fff; padding: 6px 12px; border-radius: 4px; font-family: Poppins, Segoe UI, sans-serif; font-size: 13px; font-weight: 500;">
            ${label}: ${count} (${pctStr})
          </div>`
        }
      } : {
        y: {
          formatter: (val: number) => {
            if (dataLabelDecimalPrecision > 0) {
              const formatted = val.toFixed(dataLabelDecimalPrecision)
              const cleaned = formatted.replace(/\.0+$/, '')
              return `${cleaned}%`
            }
            return `${Math.round(val)}%`
          },
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: { height: 250 },
            legend: { position: 'bottom' },
            dataLabels: { style: { fontSize: '11px' } },
          },
        },
        {
          breakpoint: 480,
          options: {
            chart: { height: 200 },
            legend: { position: 'bottom' },
            dataLabels: { style: { fontSize: '10px' } },
          },
        },
      ],
      noData: {
        text: '',
        align: 'center' as const,
        verticalAlign: 'middle' as const,
      },
    }

    if (chartInstance.current) {
      chartInstance.current.destroy()
      chartInstance.current = null
    }

    chartInstance.current = new ApexCharts(chartRef.current, options)
    chartInstance.current.render()

    const fixOverlappingLabels = () => {
      if (!chartRef.current) return

      const pieGroup = chartRef.current.querySelector('.apexcharts-pie')
      if (!pieGroup) return
      const pieBBox = (pieGroup as SVGGElement).getBBox()
      const cx = pieBBox.x + pieBBox.width / 2
      const cy = pieBBox.y + pieBBox.height / 2

      const textEls = chartRef.current.querySelectorAll('.apexcharts-pie-label')
      if (!textEls || textEls.length < 2) return

      const items = Array.from(textEls)
        .map(el => {
          const t = el as SVGTextElement
          return {
            el: t,
            x: parseFloat(t.getAttribute('x') || '0'),
            y: parseFloat(t.getAttribute('y') || '0'),
            text: t.textContent?.trim() || '',
          }
        })
        .filter(item => item.text)

      if (items.length < 2) return

      let hasOverlap = true
      let passes = 0
      const MAX_PASSES = 5

      while (hasOverlap && passes < MAX_PASSES) {
        hasOverlap = false
        passes++

        for (let i = 0; i < items.length; i++) {
          for (let j = i + 1; j < items.length; j++) {
            const a = items[i]
            const b = items[j]
            const aRect = a.el.getBoundingClientRect()
            const bRect = b.el.getBoundingClientRect()

            const hOverlap = aRect.right > bRect.left && bRect.right > aRect.left
            const vOverlap = aRect.bottom > bRect.top && bRect.bottom > aRect.top

            if (hOverlap && vOverlap) {
              hasOverlap = true

              const angleA = Math.atan2(a.y - cy, a.x - cx)
              const angleB = Math.atan2(b.y - cy, b.x - cx)
              const avgAngle = (angleA + angleB) / 2

              const perpX = -Math.sin(avgAngle)
              const perpY = Math.cos(avgAngle)

              const radX = Math.cos(avgAngle)
              const radY = Math.sin(avgAngle)

              const spreadArc = 8
              const spreadOut = 3

              const sign = angleA <= angleB ? 1 : -1

              a.x = a.x - perpX * spreadArc * sign + radX * spreadOut
              a.y = a.y - perpY * spreadArc * sign + radY * spreadOut
              a.el.setAttribute('x', String(a.x))
              a.el.setAttribute('y', String(a.y))

              b.x = b.x + perpX * spreadArc * sign + radX * spreadOut
              b.y = b.y + perpY * spreadArc * sign + radY * spreadOut
              b.el.setAttribute('x', String(b.x))
              b.el.setAttribute('y', String(b.y))
            }
          }
        }
      }
    }

    const labelFixTimer = setTimeout(fixOverlappingLabels, 400)
    return () => {
      clearTimeout(labelFixTimer)
    }
  }, [
    JSON.stringify(safeSeries),
    JSON.stringify(labels),
    height,
    JSON.stringify(colors),
    noDataMessage,
    exportFilename,
    isLoading,
    useCustomLegend,
    JSON.stringify(tooltipCounts),
    isMobile,
    legendPosition,
    legendFloating,
    legendHorizontalAlign,
    legendOffsetX,
    legendOffsetY,
    legendItemMarginHorizontal,
    legendItemMarginVertical,
    showLegend,
    dataLabelDecimalPrecision,
    dataLabelFontSize,
    dataLabelFontWeight,
    dataLabelColor,
    dataLabelDropShadow,
    dataLabelOffset,
    pieOffsetX,
    pieOffsetY,
    minAngleToShowLabel,
    type,
  ])

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        try {
          chartInstance.current.destroy()
        } catch {
        }
        chartInstance.current = null
      }
    }
  }, [
    JSON.stringify(safeSeries),
    JSON.stringify(labels),
    height,
    JSON.stringify(colors),
    exportFilename,
    useCustomLegend,
  ])

  useEffect(() => {
    if (!showLegend || useCustomLegend || !showInfoIcon) return

    let clickOutsideHandler: ((e: MouseEvent | TouchEvent) => void) | null = null
    let tooltipElement: HTMLDivElement | null = null

    const addInfoIcon = () => {
      if (!chartRef.current) return false

      const legendContainer = chartRef.current.querySelector('.apexcharts-legend')
      if (!legendContainer) return false

      if (legendContainer.querySelector('.legend-info-icon')) return true

      const infoIcon = document.createElement('span')
      infoIcon.className = 'legend-info-icon'
      infoIcon.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" style="vertical-align: middle; margin-bottom: 2px;">
          <circle cx="12" cy="12" r="10" fill="#808080"/>
          <text x="12" y="17" font-family="Arial, sans-serif" font-size="14" font-weight="bold" fill="white" text-anchor="middle">i</text>
        </svg>
      `
      infoIcon.style.cssText = 'margin-left: 6px; cursor: pointer; display: inline-flex; align-items: center; position: relative;'
      infoIcon.title = 'Click on a legend item to hide or show that data series on the chart'

      let isTooltipVisible = false

      const toggleTooltip = (e: Event) => {
        e.preventDefault()
        e.stopPropagation()
        isTooltipVisible = !isTooltipVisible

        if (!tooltipElement) {
          tooltipElement = document.createElement('div')
          tooltipElement.style.cssText = 'position: fixed; background: #333; color: #fff; padding: 8px 12px; border-radius: 6px; font-size: 12px; font-family: Poppins, Segoe UI, sans-serif; z-index: 99999; max-width: 220px; box-shadow: 0 2px 8px rgba(0,0,0,0.2); pointer-events: auto;'
          tooltipElement.textContent = 'Click on a legend item to hide or show that data series on the chart'
          document.body.appendChild(tooltipElement)
        }

        if (isTooltipVisible) {
          const rect = infoIcon.getBoundingClientRect()
          tooltipElement.style.display = 'block'
          tooltipElement.style.top = `${rect.bottom + 6}px`
          tooltipElement.style.left = `${rect.left - 100}px`
          const svgCircle = infoIcon.querySelector('circle')
          if (svgCircle) svgCircle.setAttribute('fill', '#0072B2')
          infoIcon.title = ''
        } else {
          tooltipElement.style.display = 'none'
          const svgCircle = infoIcon.querySelector('circle')
          if (svgCircle) svgCircle.setAttribute('fill', '#808080')
          infoIcon.title = 'Click on a legend item to hide or show that data series on the chart'
        }
      }

      infoIcon.addEventListener('click', toggleTooltip)
      infoIcon.addEventListener('touchend', toggleTooltip)

      clickOutsideHandler = (e: MouseEvent | TouchEvent) => {
        const target = e.target as Node
        if (!infoIcon.contains(target) && tooltipElement && !tooltipElement.contains(target) && isTooltipVisible) {
          isTooltipVisible = false
          tooltipElement.style.display = 'none'
          const svgCircle = infoIcon.querySelector('circle')
          if (svgCircle) svgCircle.setAttribute('fill', '#808080')
          infoIcon.title = 'Click on a legend item to hide or show that data series on the chart'
        }
      }
      document.addEventListener('click', clickOutsideHandler)
      document.addEventListener('touchstart', clickOutsideHandler as EventListener)

      legendContainer.appendChild(infoIcon)
      return true
    }

    const initialTimer = setTimeout(() => { addInfoIcon() }, 200)
    const intervalId = setInterval(() => { addInfoIcon() }, 500)

    return () => {
      clearTimeout(initialTimer)
      clearInterval(intervalId)
      if (clickOutsideHandler) {
        document.removeEventListener('click', clickOutsideHandler)
        document.removeEventListener('touchstart', clickOutsideHandler as EventListener)
      }
      if (tooltipElement && document.body.contains(tooltipElement)) {
        document.body.removeChild(tooltipElement)
      }
    }
  }, [showLegend, JSON.stringify(series), showInfoIcon, useCustomLegend])

  const hasData = series.length > 0 && series.some(v => v > 0)

  const CustomLegend = () => {
    if (!showLegend || !useCustomLegend) return null

    const handleLegendClick = (seriesName: string) => {
      if (chartInstance.current) {
        chartInstance.current.toggleSeries(seriesName)
      }
    }

    const handleMouseEnter = (seriesIndex: number) => {
      if (chartInstance.current) {
        chartInstance.current.toggleDataPointSelection(seriesIndex)
      }
    }

    const handleMouseLeave = (seriesIndex: number) => {
      if (chartInstance.current) {
        chartInstance.current.toggleDataPointSelection(seriesIndex)
      }
    }

    return (
      <div
        className="custom-legend"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: legendGap,
          padding: '0',
          marginTop: legendMarginTop,
          marginBottom: '4px',
          width: 'fit-content',
        }}
      >
        {labels.map((label, index) => (
          <div
            key={index}
            onClick={() => handleLegendClick(label)}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={() => handleMouseLeave(index)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: legendFontSize,
              fontWeight: legendFontWeight,
              fontFamily: 'Helvetica, Arial, sans-serif',
              lineHeight: '1.2',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            className="custom-legend-item"
          >
            <span
              style={{
                width: legendMarkerSize,
                height: legendMarkerSize,
                borderRadius: '50%',
                backgroundColor: colors[index % colors.length],
                flexShrink: 0,
              }}
            />
            <span style={{ color: '#373d3f' }}>{label}</span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={`donut-chart-container ${instanceClass}`} style={{ position: 'relative' }}>
      {legendLineHeight !== undefined && (
        <style>{`.${instanceClass} .apexcharts-legend { gap: 4px !important; row-gap: 4px !important; justify-content: flex-start !important; } .${instanceClass} .apexcharts-legend-series { line-height: ${legendLineHeight} !important; margin: 0 !important; padding: 0 !important; }`}</style>
      )}
      {title && <div className="donut-chart-title">{title}</div>}

      {legendPosition === 'top' && <CustomLegend />}

      <div ref={chartRef} />

      {legendPosition === 'bottom' && <CustomLegend />}

      {isLoading && (
        <div className="chart-loading-overlay">
          <div>Loading...</div>
        </div>
      )}

      {!isLoading && !hasData && (
        <div className="chart-loading-overlay">
          <div style={{ fontSize: '12px' }}>{noDataMessage}</div>
        </div>
      )}
    </div>
  )
})

DonutChart.displayName = 'DonutChart'

export default DonutChart
